import React, { useState, useRef, useEffect } from 'react';
import { ReactReader, ReactReaderStyle } from 'react-reader';
import { Button, Container, Row, Col, Alert, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faList, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import '../../css/EpubPreviewReader.css';

/**
 * EpubPreviewReader Component
 * 
 * A comprehensive EPUB reader with table of contents navigation
 * 
 * Usage:
 * <EpubPreviewReader 
 *   epubUrl="/path/to/book.epub" 
 *   title="Book Title" 
 * />
 * 
 * Features:
 * - Table of Contents dropdown with hierarchical navigation
 * - Progress tracking and display
 * - Fullscreen mode
 * - Responsive design
 * - Dark mode support
 * - Keyboard navigation
 */

const EpubPreviewReader = ({ epubUrl, title = "EPUB Preview" }) => {
  const [location, setLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalLocations, setTotalLocations] = useState(0);
  const [currentLocation, setCurrentLocation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [toc, setToc] = useState([]);
  const [currentSection, setCurrentSection] = useState(null);
  const renditionRef = useRef(null);
  const bookRef = useRef(null);

  // Custom styles for the reader
  const readerStyle = {
    ...ReactReaderStyle,
    reader: {
      ...ReactReaderStyle.reader,
      position: 'relative',
      height: isFullscreen ? '100vh' : '70vh',
      width: '100%',
    },
    container: {
      ...ReactReaderStyle.container,
      height: '100%',
    },
    view: {
      ...ReactReaderStyle.view,
      height: '100%',
    }
  };

  const handleLocationChange = (epubcifi) => {
    setLocation(epubcifi);
    if (renditionRef.current) {
      const { locations } = renditionRef.current;
      if (locations) {
        const current = locations.locationFromCfi(epubcifi);
        setCurrentLocation(current);
        setTotalLocations(locations.total);
      }
    }
  };

  const handleRendition = (rendition) => {
    renditionRef.current = rendition;
    setIsLoading(false);
    
    // Set up keyboard navigation
    rendition.on('keyup', (event) => {
      if (event.key === 'ArrowLeft') {
        handlePreviousPage();
      } else if (event.key === 'ArrowRight') {
        handleNextPage();
      }
    });

    // Get total locations
    rendition.on('relocated', () => {
      const { locations } = rendition;
      if (locations) {
        setTotalLocations(locations.total);
      }
    });

    // Disable text selection in the rendition iframe
    rendition.on('rendered', () => {
      const iframe = rendition.manager.container.querySelector('iframe');
      if (iframe) {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
          if (iframeDoc) {
            // Inject CSS to disable text selection in iframe
            const style = iframeDoc.createElement('style');
            style.textContent = `
              * {
                -webkit-user-select: none !important;
                -moz-user-select: none !important;
                -ms-user-select: none !important;
                user-select: none !important;
                -webkit-touch-callout: none !important;
                -webkit-tap-highlight-color: transparent !important;
                -webkit-user-drag: none !important;
                -khtml-user-drag: none !important;
                -moz-user-drag: none !important;
                -o-user-drag: none !important;
                user-drag: none !important;
              }
            `;
            iframeDoc.head.appendChild(style);
            
            // Also disable selection events
            iframeDoc.addEventListener('selectstart', (e) => e.preventDefault());
            iframeDoc.addEventListener('dragstart', (e) => e.preventDefault());
          }
        } catch (error) {
          // Cross-origin iframe - this is expected for some EPUB files
          console.log('Cannot access iframe content (cross-origin):', error.message);
        }
      }
    });

  };

  const handleBook = (book) => {
    bookRef.current = book;
  };

  const handleTocChanged = (toc) => {
    console.log('TOC received:', toc);
    const tocItems = [];
    
    // Recursive function to process TOC items with their subitems
    const processTocItem = (item, level = 0, parentId = null) => {
      if (item.href) {
        tocItems.push({
          id: item.id || `toc-${tocItems.length}`,
          label: item.label,
          href: item.href,
          level: level,
          parent: item.parent || parentId,
          hasChildren: item.subitems && item.subitems.length > 0
        });
      }
      
      // Process subitems recursively
      if (item.subitems && item.subitems.length > 0) {
        item.subitems.forEach(subItem => {
          processTocItem(subItem, level + 1, item.id);
        });
      }
    };
    
    // Process all top-level TOC items
    toc.forEach((chapter, index) => {
      processTocItem(chapter, 0);
    });
    
    console.log('TOC items processed:', tocItems);
    setToc(tocItems);
  };

  const handleTocNavigation = (tocItem) => {
    if (renditionRef.current && tocItem.href) {
      renditionRef.current.display(tocItem.href);
    }
  };

  const handleNextPage = () => {
    if (renditionRef.current) {
      renditionRef.current.next();
    }
  };

  const handlePreviousPage = () => {
    if (renditionRef.current) {
      renditionRef.current.prev();
    }
  };

  const handleError = (error) => {
    console.error('EPUB Reader Error:', error);
    setError('Failed to load EPUB file. Please check the file URL and try again.');
    setIsLoading(false);
  };

  const formatProgress = () => {
    if (totalLocations === 0) return '0%';
    const percentage = Math.round((currentLocation / totalLocations) * 100);
    return `${percentage}%`;
  };

  useEffect(() => {
    if (epubUrl) {
      setIsLoading(false);
      setError(null);
    }
  }, [epubUrl]);

  // Update current section tracking when TOC changes
  useEffect(() => {
    if (renditionRef.current && toc.length > 0) {
      renditionRef.current.on('rendered', (section) => {
        const currentTocItem = toc.find(item => item.href === section.href);
        if (currentTocItem) {
          setCurrentSection(currentTocItem.href);
        }
      });
    }
  }, [toc]);

  if (!epubUrl) {
    return (
      <Container className="epub-reader-container">
        <Alert variant="warning" className="text-center">
          <FontAwesomeIcon icon={faBookOpen} className="me-2" />
          No EPUB file provided for preview
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className={`epub-reader-container ${isFullscreen ? 'fullscreen' : ''}`}>
      {/* Header Controls */}
      <Row className="epub-controls">

        <Col className="text-center">
          {/* Table of Contents Dropdown */}
          <Dropdown className="toc-dropdown">
            <Dropdown.Toggle variant="outline-primary" id="toc-dropdown" className="toc-toggle">
              <FontAwesomeIcon icon={faList} className="me-2" />
              Table of Contents
              <FontAwesomeIcon icon={faChevronDown} className="ms-2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="toc-menu">
              {toc.length > 0 ? (
                toc.map((item, index) => (
                  <Dropdown.Item
                    key={item.id || index}
                    onClick={() => handleTocNavigation(item)}
                    className={`toc-item level-${item.level} ${
                      currentSection === item.href ? 'current-section' : ''
                    }`}
                  >
                    <div className="toc-item-content">
                      {item.hasChildren && (
                        <FontAwesomeIcon 
                          icon={faChevronRight} 
                          className="toc-expand-icon me-2" 
                        />
                      )}
                      <span className="toc-item-label">{item.label}</span>
                    </div>
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled>
                  No table of contents available
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
        
      </Row>


      {/* EPUB Reader */}
      <Row>
        <Col>
          <div className="epub-reader-wrapper">
            {error ? (
              <Alert variant="danger" className="text-center">
                {error}
              </Alert>
            ) : (
              <>
                {isLoading && (
                  <div className="epub-loading text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading EPUB...</p>
                  </div>
                )}
                <div style={{ height: isFullscreen ? 'calc(100vh - 200px)' : '65vh' }}>
                  <ReactReader
                    url={epubUrl}
                    location={location}
                    locationChanged={handleLocationChange}
                    getRendition={handleRendition}
                    getBook={handleBook}
                    tocChanged={handleTocChanged}
                    epubOptions={{
                      allowScriptedContent: true,
                      allowPopups: false,
                    }}
                    readerStyle={readerStyle}
                    loadingView={<div className="text-center">Loading EPUB...</div>}
                    showToc={false}
                    epubInitOptions={{
                      openAs: 'epub',
                    }}
                    onError={handleError}
                  />
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>

      {/* Progress Bar */}
      <Row className="mt-3">
        <Col>
          <div className="epub-progress-bar">
            <div 
              className="progress-bar-fill" 
              style={{ width: formatProgress() }}
            ></div>
          </div>
        </Col>
      </Row>

      {/* Keyboard Shortcuts Info */}
      <Row className="mt-3">
        <Col>
          <small className="text-muted">
            <strong>Keyboard Shortcuts:</strong> Use ← → arrow keys to navigate pages
          </small>
        </Col>
      </Row>
    </Container>
  );
};

export default EpubPreviewReader;
